import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useMemo, useRef } from 'react'
import apiClient from '@services/api/client'
import NotificationBell from '../components/admin/NotificationBell'
import MessageBell from '../components/admin/MessageBell'

const _storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const buildPhotoUrl = (p) => !p ? null : p.startsWith('http') || p.startsWith('/') ? p : `${_storageURL}/${p}`
import { useAuthStore } from '@store'

function AdminLayout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore() // Utiliser le store d'authentification
  const [showNotifications, setShowNotifications] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showQuickAccess, setShowQuickAccess] = useState(false)
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  const [notifications, setNotifications] = useState([])
  // user.role est un objet Eloquent {id, name, ...} — extraire le nom
  const resolvedRoleName = typeof user?.role === 'object' ? user?.role?.name : user?.role
  const [currentUser, setCurrentUser] = useState({
    name: user?.nom || user?.prenom || user?.name || 'Admin',
    role: resolvedRoleName || 'Admin',
    roles: user?.roles || (resolvedRoleName ? [resolvedRoleName] : []),
    photo: buildPhotoUrl(user?.photo_profil || user?.photo) || null,
    client_id: user?.client_id || user?.users_id || null
  })
  const [rolesData, setRolesData] = useState([])
  const [pendingVerificationCount, setPendingVerificationCount] = useState(0)
  const navRef = useRef(null)

  // Garder l'onglet actif visible dans la sidebar (scroll automatique)
  useEffect(() => {
    const active = navRef.current?.querySelector('[data-active="true"]')
    if (active) active.scrollIntoView({ block: 'nearest' })
  }, [location.pathname])
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0)
  const [newActivitiesCount, setNewActivitiesCount] = useState(0)
  const isBlockedRef = useRef(false) // Bloquer le rechargement pendant le marquage

  // Charger les rôles avec leurs icônes depuis la base de données
  const loadRolesData = async () => {
    try {
      const data = await apiClient.get('/roles')
      if (data.success) {
        setRolesData(data.data || [])
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rôles:', error)
    }
  }

  useEffect(() => {
    // Charger les rôles
    loadRolesData()
    
    // Vérifier si l'utilisateur est connecté
    const adminUser = localStorage.getItem('admin_user')
    const adminToken = localStorage.getItem('admin_token')
    
    if (!adminUser || !adminToken) {
      navigate('/admin/login')
      return
    }
    
    // Charger les infos utilisateur
    try {
      const userData = JSON.parse(adminUser)
      // role peut être un objet Eloquent {id, name, ...} ou une chaîne
      const roleObj = userData.role
      const roleName = typeof roleObj === 'object' && roleObj !== null
        ? roleObj.name
        : (roleObj || null)
      const userRoles = Array.isArray(userData.roles)
        ? userData.roles
        : (roleName ? [roleName] : ['Admin'])

      setCurrentUser({
        name: [userData.prenom, userData.nom].filter(Boolean).join(' ') || 'Admin',
        role: userRoles[0] || roleName || 'Admin',
        roles: userRoles,
        photo: buildPhotoUrl(userData.photo_profil) || null,
        client_id: userData.client_id || userData.users_id || userData.id
      })
      
      // TOUJOURS synchroniser le store Zustand pour les composants (NotificationBell, MessageBell)
      const { setUser, setToken } = useAuthStore.getState()
      // S'assurer que client_id est toujours défini
      const userForStore = {
        ...userData,
        client_id: userData.client_id || userData.id,
        id: userData.client_id || userData.id
      }
      setUser(userForStore)
      setToken(adminToken)
      console.log('[AdminLayout] User synchronisé avec le store:', userForStore.client_id)
      
      // Charger le nombre de notifications et messages non lus
      loadUnreadCounts()
    } catch (error) {
      console.error('Erreur parsing user:', error)
    }
  }, [navigate])
  
  // Fonction pour charger les compteurs de non lus
  const loadUnreadCounts = async () => {
    if (isBlockedRef.current || localStorage.getItem('isMarkingNotifications') === 'true') {
      const forcedCount = parseInt(localStorage.getItem('notificationCount') || '0')
      setUnreadNotifications(forcedCount)
      setNewActivitiesCount(forcedCount)
      return
    }

    try {
      // Notifications non lues
      const notifData = await apiClient.get('/notifications/unread-count')
      if (notifData.success) {
        setUnreadNotifications(notifData.count || 0)
        setNewActivitiesCount(notifData.count || 0)
      }
    } catch (_) { /* silencieux si non dispo */ }

    try {
      // Publications en attente de validation
      const pendingData = await apiClient.get('/posts/pending')
      if (pendingData.success) {
        setPendingVerificationCount(pendingData.total || pendingData.data?.length || 0)
      }
    } catch (_) { /* silencieux */ }

    try {
      // Messages non lus
      const msgData = await apiClient.get('/messages/unread')
      if (msgData.success) {
        setUnreadMessagesCount(msgData.count || 0)
      }
    } catch (_) { /* silencieux */ }
  }
  
  // Charger les compteurs au montage puis toutes les 30 secondes
  useEffect(() => {
    loadUnreadCounts()
    const interval = setInterval(loadUnreadCounts, 30000)
    return () => clearInterval(interval)
  }, [])

  // Recharger quand la fenêtre redevient active
  useEffect(() => {
    const handleFocus = () => {
      if (currentUser.client_id) {
        loadUnreadCounts()
      }
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [currentUser.client_id])

  // Écouter les événements de marquage des notifications
  useEffect(() => {
    // Événement pour bloquer/débloquer le rechargement
    const handleBlockReload = (e) => {
      isBlockedRef.current = e.detail.blocked
      console.log('[AdminLayout] Rechargement', e.detail.blocked ? 'BLOQUÉ' : 'DÉBLOQUÉ')
    }
    
    // Événement quand toutes les notifications sont marquées comme lues
    const handleAllMarkedRead = () => {
      console.log('[AdminLayout] Toutes les notifications marquées comme lues - Mise à jour à 0')
      setUnreadNotifications(0)
      setNewActivitiesCount(0)
    }
    
    // Événement quand une notification est lue
    const handleNotificationRead = () => {
      console.log('[AdminLayout] Notification marquée comme lue')
      if (currentUser.client_id) {
        setTimeout(() => loadUnreadCounts(), 500)
      }
    }
    
    // Événement pour recharger les messages
    const handleReloadMessages = () => {
      console.log('[AdminLayout] Rechargement des messages demandé')
      if (currentUser.client_id) {
        loadUnreadCounts()
      }
    }
    
    window.addEventListener('blockNotificationReload', handleBlockReload)
    window.addEventListener('notificationMarkedAllRead', handleAllMarkedRead)
    window.addEventListener('notificationRead', handleNotificationRead)
    window.addEventListener('reloadMessages', handleReloadMessages)
    
    return () => {
      window.removeEventListener('blockNotificationReload', handleBlockReload)
      window.removeEventListener('notificationMarkedAllRead', handleAllMarkedRead)
      window.removeEventListener('notificationRead', handleNotificationRead)
      window.removeEventListener('reloadMessages', handleReloadMessages)
    }
  }, [currentUser.client_id])

  // Fonction pour obtenir l'icône du rôle (avec Material Icons dynamiques)
  const getRoleIcon = (role, roleIcon = null) => {
    // Si une icône personnalisée est fournie, utiliser Material Icons
    if (roleIcon) {
      return (
        <span className="material-icons" style={{ fontSize: '16px' }}>
          {roleIcon}
        </span>
      )
    }
    
    // Sinon, utiliser les icônes par défaut
    const defaultIcons = {
      'Super Admin': 'admin_panel_settings',
      'Admin': 'shield_person',
      'Superviseur': 'supervisor_account',
      'Animateur': 'support_agent',
      'Technicien': 'engineering',
      'Client': 'account_circle'
    }
    
    const iconName = defaultIcons[role] || 'person'
    return (
      <span className="material-icons" style={{ fontSize: '16px' }}>
        {iconName}
      </span>
    )
  }

  // Fonction pour obtenir la couleur du rôle
  const getRoleColor = (role) => {
    const roleColors = {
      'Super Admin': 'text-yellow-600 bg-yellow-50',
      'Admin': 'text-purple-600 bg-purple-50',
      'Superviseur': 'text-blue-600 bg-blue-50',
      'Animateur': 'text-green-600 bg-green-50',
      'Technicien': 'text-gray-600 bg-gray-50',
      'Client': 'text-indigo-600 bg-indigo-50'
    }
    return roleColors[role] || 'text-gray-600 bg-gray-50'
  }

  // Fonction pour vérifier si l'utilisateur peut accéder à un menu
  const canAccessMenu = (menuPath) => {
    const userRoles = currentUser.roles || []
    
    // Super Admin : accès à tout
    if (userRoles.some(r => r.toLowerCase() === 'super admin')) {
      return true
    }
    
    // Technicien : accès aux communications, gestion, sécurité, système et profil
    if (userRoles.some(r => r.toLowerCase() === 'technicien')) {
      const allowedPaths = [
        '/admin/clients',
        '/admin/users',
        '/admin/messages',
        '/admin/activities',
        '/admin/security/reports',
        '/admin/security/personal',
        '/admin/security/system',
        '/admin/system-control',
        '/admin/profile',
        '/admin/settings',
        '/admin/help'
      ]
      return allowedPaths.includes(menuPath)
    }
    
    // Admin : accès à tout sauf Contrôle Système et Sécurité Système
    if (userRoles.some(r => r.toLowerCase() === 'admin')) {
      const restrictedPaths = ['/admin/system-control', '/admin/security/system']
      return !restrictedPaths.includes(menuPath)
    }
    
    // Superviseur : pas d'accès aux Clients, Utilisateurs, Sécurité Système et Système
    if (userRoles.some(r => r.toLowerCase() === 'superviseur')) {
      const restrictedPaths = ['/admin/clients', '/admin/users', '/admin/system-control', '/admin/security/system']
      return !restrictedPaths.includes(menuPath)
    }
    
    // Animateur : accès limité
    if (userRoles.some(r => r.toLowerCase() === 'animateur')) {
      const allowedPaths = [
        '/admin/dashboard',
        '/admin/publications',
        '/admin/videos',
        '/admin/banners',
        '/admin/categories',
        '/admin/messages',
        '/admin/activities',
        '/admin/profile',
        '/admin/settings',
        '/admin/help'
      ]
      return allowedPaths.includes(menuPath)
    }
    
    // Par défaut, pas d'accès
    return false
  }

  const menuCategories = [
    {
      title: 'Principal',
      items: [
        { 
          path: '/admin/dashboard', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 12a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7z" />
            </svg>
          ), 
          label: 'Tableau de bord' 
        }
      ]
    },
    {
      title: 'Gestion',
      items: [
        { 
          path: '/admin/clients', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ), 
          label: 'Gestionnaire des clients' 
        },
        { 
          path: '/admin/users', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ), 
          label: 'Gestionnaire des utilisateurs' 
        }
      ]
    },
    {
      title: 'Contenu',
      items: [
        { 
          path: '/admin/verification', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ), 
          label: 'Vérification',
          badge: true,
          badgeCount: pendingVerificationCount
        },
        { 
          path: '/admin/publications', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ), 
          label: 'Publications' 
        },
        { 
          path: '/admin/videos', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          ), 
          label: 'Vidéos' 
        },
        { 
          path: '/admin/banners', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          ), 
          label: 'Bannières' 
        },
        { 
          path: '/admin/categories', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          ), 
          label: 'Catégories',
          allowedRoles: ['Super Admin', 'Admin', 'Superviseur']
        },
        { 
          path: '/admin/roles', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ), 
          label: 'Rôles',
          allowedRoles: ['Super Admin', 'Technicien']
        }
      ]
    },
    {
      title: 'Communication',
      items: [
        { 
          path: '/admin/messages', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          ), 
          label: 'Messages',
          badge: true,
          badgeCount: unreadMessagesCount
        },
        { 
          path: '/admin/activities', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ), 
          label: 'Journal des activités',
          badge: true,
          badgeCount: newActivitiesCount
        }
      ]
    },
    {
      title: 'Finance',
      items: [
        { 
          path: '/admin/pricing', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ), 
          label: 'Tarification',
          allowedRoles: ['Super Admin', 'Admin', 'Superviseur']
        },
        { 
          path: '/admin/tarif', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ), 
          label: 'Tarif',
          allowedRoles: ['Super Admin', 'Admin', 'Superviseur']
        }
      ]
    },
    {
      title: 'Système',
      items: [
        { 
          path: '/admin/security/reports', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ), 
          label: 'Rapports' 
        },
        { 
          path: '/admin/security/personal', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          ), 
          label: 'Ma Sécurité',
          allowedRoles: ['Super Admin', 'Technicien']
        },
        { 
          path: '/admin/security/system', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          ), 
          label: 'Sécurité Système' 
        },
        { 
          path: '/admin/system-control', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          ), 
          label: 'Contrôle Système' 
        },
        { 
          path: '/admin/profile', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ), 
          label: 'Mon profil' 
        },
        { 
          path: '/admin/settings', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          ), 
          label: 'Paramètres' 
        },
        {
          path: '/admin/help',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          label: 'Aide'
        },
        {
          path: '/admin/terms',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          label: 'Conditions générales',
          allowedRoles: ['Super Admin', 'Admin']
        },
        {
          path: '/admin/privacy',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          ),
          label: 'Confidentialité',
          allowedRoles: ['Super Admin', 'Admin']
        }
      ]
    }
  ]

  // Filtrer les menus selon les permissions
  const filteredMenuCategories = useMemo(() => {
    return menuCategories
      .map(category => ({
        ...category,
        items: category.items.filter(item => {
          // Vérifier d'abord si l'item a des rôles spécifiques requis
          if (item.allowedRoles && item.allowedRoles.length > 0) {
            const userRoles = currentUser.roles || []
            const hasRequiredRole = userRoles.some(userRole => 
              item.allowedRoles.some(allowedRole => 
                userRole.toLowerCase() === allowedRole.toLowerCase()
              )
            )
            if (!hasRequiredRole) {
              return false
            }
          }
          // Ensuite vérifier les permissions par path
          return canAccessMenu(item.path)
        })
      }))
      .filter(category => category.items.length > 0) // Retirer les catégories vides
  }, [menuCategories, currentUser.roles])

  return (
    <div className="home-font min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-80 bg-[#002A4E] shadow-2xl z-20 flex flex-col overflow-hidden">
        {/* Logo - Fixed */}
        <div className="p-6 pb-4 flex-shrink-0">
          <div className="flex flex-col items-start gap-2 pb-5 border-b border-white/10">
            <img
              src="/Logo_LIGDI_blanc.png"
              alt="KUDHI"
              className="h-28 w-auto max-w-full object-contain"
            />
            <div>
              <p className="rounded-md bg-[#FFD700] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#002A4E]">Admin</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav ref={navRef} className="flex-1 overflow-y-auto py-2 space-y-5 scrollbar-hide">
          {filteredMenuCategories.map((category, index) => (
            <div key={index}>
              <h3 className="text-[11px] font-bold text-white/40 uppercase tracking-widest px-6 mb-2">
                {category.title}
              </h3>
              <div className="space-y-0.5">
                {category.items.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-active={location.pathname === item.path}
                    className={`relative w-full flex items-center gap-3 px-6 py-2.5 transition-all duration-200 ${
                      location.pathname === item.path
                        ? 'bg-[#FFD700] text-[#002A4E] font-semibold'
                        : 'hover:bg-white/5 text-white/70 hover:text-white'
                    }`}
                  >
                    <div className={`p-1.5 rounded-md ${
                      location.pathname === item.path
                        ? 'bg-[#002A4E]/10'
                        : 'bg-white/5 text-[#FFD700]'
                    }`}>
                      {item.icon}
                    </div>
                    <span className="text-[15px] font-medium flex-1 text-left">{item.label}</span>
                    {item.badge && item.badgeCount > 0 && (
                      <span className="flex-shrink-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-[#002A4E] bg-red-600 rounded-full min-w-[20px] ">
                        {item.badgeCount > 99 ? '99+' : item.badgeCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer - Fixed */}

        <div className="p-6 border-t border-white/10">
          <button
            onClick={async () => {
              try { await apiClient.post('/logout') } catch (_) {}
              localStorage.removeItem('admin_user')
              localStorage.removeItem('admin_token')
              useAuthStore.setState({ user: null, token: null, userType: null, isAuthenticated: false })
              navigate('/admin/login')
            }}
            className="w-full flex items-center gap-3 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 rounded-md text-red-400 hover:text-red-300 font-medium transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-80 min-h-screen bg-slate-50">
        {/* Top Navigation Bar */}
        <div className="fixed top-0 right-0 left-80 bg-white/95 backdrop-blur border-b border-slate-200 px-8 py-4 z-10">
          <div className="flex items-center justify-between">
            {/* Search Bar */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-4 ml-6">
              {/* Quick Access Button */}
              <div className="relative">
                <button 
                  onClick={() => {
                    setShowQuickAccess(!showQuickAccess)
                    setShowNotifications(false)
                    setShowProfileMenu(false)
                  }}
                  className="p-2 bg-[#FFD700] hover:bg-[#F5C800] text-[#002A4E] rounded-md transition-colors "
                  title="Accès rapide"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>

                {/* Quick Access Modal */}
                {showQuickAccess && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-2xl border border-gray-200 py-3 z-50 animate-slideUp">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-800">Accès rapide</h3>
                      <p className="text-xs text-gray-500">Créer un nouveau contenu</p>
                    </div>
                    <div className="py-2">
                      {/* Nouveau Article */}
                      <button 
                        onClick={() => {
                          const currentPath = location.pathname
                          if (currentPath === '/admin/publications') {
                            // Déclencher l'ouverture du modal si on est déjà sur la page
                            window.dispatchEvent(new CustomEvent('openAddPublicationModal'))
                          } else {
                            // Naviguer vers la page puis ouvrir le modal
                            navigate('/admin/publications')
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('openAddPublicationModal'))
                            }, 100)
                          }
                          setShowQuickAccess(false)
                        }}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-800 block">Nouveau Article</span>
                          <span className="text-xs text-gray-500">Créer une publication</span>
                        </div>
                      </button>

                      {/* Nouvelle Bannière */}
                      <button 
                        onClick={() => {
                          const currentPath = location.pathname
                          if (currentPath === '/admin/banners') {
                            window.dispatchEvent(new CustomEvent('openAddBannerModal'))
                          } else {
                            navigate('/admin/banners')
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('openAddBannerModal'))
                            }, 100)
                          }
                          setShowQuickAccess(false)
                        }}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-purple-50 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-800 block">Nouvelle Bannière</span>
                          <span className="text-xs text-gray-500">Créer une bannière pub</span>
                        </div>
                      </button>

                      {/* Nouvelle Vidéo */}
                      <button 
                        onClick={() => {
                          const currentPath = location.pathname
                          if (currentPath === '/admin/videos') {
                            window.dispatchEvent(new CustomEvent('openAddVideoModal'))
                          } else {
                            navigate('/admin/videos')
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('openAddVideoModal'))
                            }, 100)
                          }
                          setShowQuickAccess(false)
                        }}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-red-50 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-800 block">Nouvelle Vidéo</span>
                          <span className="text-xs text-gray-500">Ajouter une vidéo</span>
                        </div>
                      </button>

                      {/* Nouveau Client */}
                      <button 
                        onClick={() => {
                          const currentPath = location.pathname
                          if (currentPath === '/admin/clients') {
                            window.dispatchEvent(new CustomEvent('openAddClientModal'))
                          } else {
                            navigate('/admin/clients')
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('openAddClientModal'))
                            }, 100)
                          }
                          setShowQuickAccess(false)
                        }}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-green-50 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-800 block">Nouveau Client</span>
                          <span className="text-xs text-gray-500">Ajouter un client</span>
                        </div>
                      </button>

                      {/* Nouveau Utilisateur */}
                      <button 
                        onClick={() => {
                          const currentPath = location.pathname
                          if (currentPath === '/admin/users') {
                            window.dispatchEvent(new CustomEvent('openAddUserModal'))
                          } else {
                            navigate('/admin/users')
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('openAddUserModal'))
                            }, 100)
                          }
                          setShowQuickAccess(false)
                        }}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-orange-50 rounded-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-800 block">Nouveau Utilisateur</span>
                          <span className="text-xs text-gray-500">Créer un utilisateur admin</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages Bell */}
              <MessageBell />

              {/* Notifications Dropdown */}
              <NotificationBell />

              {/* Notifications Dropdown Menu removed - using NotificationBell component instead */}
              {showNotifications && false && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-2xl border border-gray-200 py-2 z-50 animate-slideUp">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-800">Notifications</h3>
                      <p className="text-xs text-gray-500">
                        {unreadNotifications > 0 
                          ? `Vous avez ${unreadNotifications} nouvelle${unreadNotifications > 1 ? 's' : ''} notification${unreadNotifications > 1 ? 's' : ''}`
                          : 'Aucune nouvelle notification'
                        }
                      </p>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notif, index) => {
                          const iconMap = {
                            user_login: { path: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', bg: 'bg-blue-100', color: 'text-blue-600' },
                            post_approved: { path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', bg: 'bg-green-100', color: 'text-green-600' },
                            post_rejected: { path: 'M6 18L18 6M6 6l12 12', bg: 'bg-red-100', color: 'text-red-600' },
                            post_pending: { path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', bg: 'bg-yellow-100', color: 'text-yellow-600' },
                            post_published: { path: 'M5 13l4 4L19 7', bg: 'bg-purple-100', color: 'text-purple-600' },
                            default: { path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', bg: 'bg-gray-100', color: 'text-gray-600' }
                          }
                          
                          const icon = iconMap[notif.type] || iconMap.default
                          
                          return (
                            <button 
                              key={notif.id || index}
                              onClick={() => {
                                if (notif.link) {
                                  navigate(notif.link)
                                } else {
                                  navigate('/admin/activities')
                                }
                                setShowNotifications(false)
                              }}
                              className={`w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex gap-3 ${notif.is_read === 0 ? 'bg-blue-50' : ''}`}
                            >
                              <div className={`w-10 h-10 ${icon.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                                <svg className={`w-5 h-5 ${icon.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon.path} />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 line-clamp-1">{notif.title || 'Notification'}</p>
                                <p className="text-xs text-gray-500 line-clamp-2">{notif.message || ''}</p>
                                <p className="text-xs text-gray-400 mt-1">{notif.time_ago || ''}</p>
                              </div>
                            </button>
                          )
                        })
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          Aucune notification pour le moment
                        </div>
                      )}
                    </div>
                    <div className="px-4 py-2 border-t border-gray-100">
                      <button 
                        onClick={() => {
                          navigate('/admin/activities')
                          setShowNotifications(false)
                        }}
                        className="w-full text-center text-sm text-purple-600 hover:text-purple-700 font-medium py-1"
                      >
                        Voir toutes les notifications
                      </button>
                    </div>
                  </div>
                )}
              
              {/* Profile Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => {
                    setShowProfileMenu(!showProfileMenu)
                    setShowNotifications(false)
                  }}
                  className="flex items-center gap-3 pl-4 border-l border-gray-200 hover:bg-gray-50 rounded-md p-2 transition-colors"
                >
                  <div className="relative w-10 h-10 flex-shrink-0">
                    {currentUser.photo && (
                      <img
                        src={currentUser.photo}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover border-2 border-[#F5B700] absolute inset-0"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextElementSibling.style.display = 'flex'
                        }}
                      />
                    )}
                    <div
                      className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-full items-center justify-center"
                      style={{ display: currentUser.photo ? 'none' : 'flex' }}
                    >
                      <span className="text-[#002A4E] font-bold text-sm">
                        {currentUser.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-sm text-gray-900">{currentUser.name}</p>
                    <div className={`flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full w-fit ${getRoleColor(currentUser.role)}`}>
                      {getRoleIcon(currentUser.role, rolesData.find(r => r.name === currentUser.role)?.icon)}
                      <span className="font-medium">{currentUser.role}</span>
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-2xl border border-gray-200 py-2 z-50 animate-slideUp">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-semibold text-gray-800">{currentUser.name}</p>
                      <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full w-fit mt-1 ${getRoleColor(currentUser.role)}`}>
                        {getRoleIcon(currentUser.role, rolesData.find(r => r.name === currentUser.role)?.icon)}
                        <span className="font-medium">{currentUser.role}</span>
                      </div>
                    </div>
                    <div className="py-2">
                      <button 
                        onClick={() => {
                          navigate('/admin/profile')
                          setShowProfileMenu(false)
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-sm text-gray-700">Mon profil</span>
                      </button>
                      <button 
                        onClick={() => {
                          navigate('/admin/settings')
                          setShowProfileMenu(false)
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm text-gray-700">Paramètres</span>
                      </button>
                      <button 
                        onClick={() => {
                          navigate('/admin/help')
                          setShowProfileMenu(false)
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-gray-700">Aide</span>
                      </button>
                    </div>
                    <div className="border-t border-gray-100 pt-2">
                      <button
                        onClick={async () => {
                          try { await apiClient.post('/logout') } catch (_) {}
                          localStorage.removeItem('admin_user')
                          localStorage.removeItem('admin_token')
                          useAuthStore.setState({ user: null, token: null, userType: null, isAuthenticated: false })
                          navigate('/admin/login')
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-red-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className="text-sm font-medium">Déconnexion</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="pt-20 p-8">
          {children}
        </div>
      </main>
    </div>
  )
}

export default AdminLayout
